/**
 * Chutt... - Objective Card
 * Card showing objective name, gauge, values and progress
 */
import React from 'react';
import { Box, Paper, Typography, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import CircularObjectiveGauge from './CircularObjectiveGauge';
import { colors } from '../../styles/colors';

const getStatusConfig = (progress) => {
  if (progress >= 100) return { label: 'Atteint', color: colors.success, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
  if (progress >= 70) return { label: 'En bonne voie', color: colors.teal };
  if (progress >= 40) return { label: 'En cours', color: colors.warning };
  return { label: 'En retard', color: colors.danger, icon: <FlagIcon sx={{ fontSize: 14 }} /> };
};

function ObjectiveCard({ name, progress, current, target, unit, type }) {
  const status = getStatusConfig(progress || 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        height: '100%',
        bgcolor: colors.white,
        position: 'relative',
        transition: 'border-color 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: status.color,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularObjectiveGauge
          value={progress}
          current={current || 0}
          target={target || 1}
          unit={unit || ''}
          size={60}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: colors.charcoal,
              fontSize: '0.85rem',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500, display: 'block', mb: 1 }}>
            {Math.round(current || 0)} / {Math.round(target || 0)} {unit}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, progress || 0)}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: colors.gray100,
              '& .MuiLinearProgress-bar': {
                bgcolor: status.color,
                borderRadius: 2,
                transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {status.icon}
            <Typography variant="caption" sx={{ color: status.color, fontWeight: 500, fontSize: '0.7rem' }}>
              {status.label}
            </Typography>
          </Box>
        </Box>
      </Box>
      {(progress || 0) >= 100 && (
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          animation: 'scaleIn 0.3s ease-out',
          '@keyframes scaleIn': {
            '0%': { transform: 'scale(0)' },
            '100%': { transform: 'scale(1)' },
          },
        }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: colors.success }} />
        </Box>
      )}
    </Paper>
  );
}

export default ObjectiveCard;
